import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { StoryblokImage } from "@wojo/storyblok";
import { Button, Text } from "@wojo/ui";
import cn from "clsx";
import React, { useEffect, useId, useRef, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useTourPageContext } from "../../_client/context";
import { TourSectionId } from "../../_client/hooks";
import styles from "./Itinerary.module.scss";
import { ItineraryDay } from "./ItineraryDay";
import { ItineraryExtension } from "./ItineraryExtension";
import { ItinerarySectionTitle } from "./ItinerarySectionTitle";
/**
 * Number of days to show by default
 */
const NB_DAYS_TO_SHOW = 2;

// 670px is the max width of desktop image, multiple by 2 for high density screens
const IMAGE_WIDTH_DESKTOP = 670 * 2;
// 991 is the max width of mobile image, multiple by 2 for high density screens
const IMAGE_WIDTH_MOBILE = 991 * 2;

export const Itinerary: React.FC = () => {
    const { tourContent, pdfUrl } = useTourPageContext();
    const { productPage } = useI18n("productPage");
    const [itineraryExpanded, setItineraryExpanded] = useState(false);
    const [afterFirstClick, setAfterFirstClick] = useState(false);
    const sectionTitleId = useId();
    const revealedElRef = useRef<HTMLDivElement>(null);
    const toggleElRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (itineraryExpanded) {
            revealedElRef.current?.focus();
        } else {
            if (afterFirstClick) {
                toggleElRef.current?.scrollIntoView({ block: "center" });
            }
        }
    }, [itineraryExpanded, afterFirstClick]);

    const mapImage = tourContent.mapImage;
    const extension = tourContent.extension;

    let nbDaysRendered = 0;
    const incrementDaysRendered = () => {
        nbDaysRendered = nbDaysRendered + 1;
    };

    const itineraryButtonText = itineraryExpanded
        ? productPage.itinerary.expandedToggleLabel.l()
        : productPage.itinerary.collapsedToggleLabel.l();

    return (
        <section
            className={styles.section}
            aria-labelledby={sectionTitleId}
            id={TourSectionId.ITINERARY}
            tabIndex={-1}
        >
            <Text.Heading
                order="3"
                tag="h2"
                id={sectionTitleId}
                style={{ marginBottom: "var(--g-spacing-md)" }}
            >
                {productPage.itinerary.heading.l()}
            </Text.Heading>

            {!!pdfUrl && (
                <Button
                    tag="a"
                    href={pdfUrl}
                    target="_blank"
                    size="small"
                    variant="quiet"
                    adornRight={<FiDownload aria-hidden />}
                    className={styles["print-button"]}
                >
                    {productPage.itinerary.printItinerary.l()}
                </Button>
            )}

            {!!mapImage?.filename && (
                <StoryblokImage
                    style={{
                        maxWidth: "100%",
                        border: "1px solid var(--s-color-separator-standard)",
                        marginBottom: "var(--g-spacing-xl)",
                    }}
                    mobile={{
                        asset: mapImage,
                        size: { width: IMAGE_WIDTH_MOBILE },
                    }}
                    desktop={{
                        asset: mapImage,
                        size: { width: IMAGE_WIDTH_DESKTOP },
                    }}
                />
            )}
            <div
                className={cn(styles["day-by-day"], {
                    [styles["day-by-day--expanded"]]: itineraryExpanded,
                })}
            >
                {tourContent.itineraryItems.map((day, index) => {
                    if (
                        nbDaysRendered == NB_DAYS_TO_SHOW &&
                        !itineraryExpanded
                    ) {
                        return null;
                    }
                    const titleId = `day-title-${sectionTitleId}-${index}`;
                    if (
                        day.__typename === "TourItineraryItemContent" &&
                        day.days.length > 0
                    ) {
                        return (
                            <div
                                key={titleId}
                                className={cn(
                                    styles.day,
                                    styles["day--overnight"],
                                )}
                                aria-labelledby={
                                    day.title ? titleId : undefined
                                }
                            >
                                <ItinerarySectionTitle
                                    title={day.title ?? undefined}
                                    isTopLevel
                                    nbNights={day.nbNights}
                                    id={titleId}
                                />
                                {day.days.map(
                                    (overnightDay, overnightIndex) => {
                                        if (
                                            nbDaysRendered == NB_DAYS_TO_SHOW &&
                                            !itineraryExpanded
                                        ) {
                                            return null;
                                        }
                                        incrementDaysRendered();
                                        return (
                                            <ItineraryDay
                                                key={`inner-day-${sectionTitleId}-${overnightIndex}`}
                                                {...overnightDay}
                                                isTopLevel={!day.title}
                                                ref={
                                                    nbDaysRendered ===
                                                    NB_DAYS_TO_SHOW + 1
                                                        ? revealedElRef
                                                        : undefined
                                                }
                                            />
                                        );
                                    },
                                )}
                            </div>
                        );
                    } else {
                        if (
                            nbDaysRendered == NB_DAYS_TO_SHOW &&
                            !itineraryExpanded
                        ) {
                            return null;
                        }
                        incrementDaysRendered();
                        return (
                            <ItineraryDay
                                {...day}
                                key={titleId}
                                isTopLevel={true}
                                ref={
                                    nbDaysRendered === NB_DAYS_TO_SHOW + 1
                                        ? revealedElRef
                                        : undefined
                                }
                            />
                        );
                    }
                })}
            </div>
            {itineraryExpanded && <ItineraryExtension extension={extension} />}
            <Button
                type="button"
                variant="secondary"
                aria-expanded={!!itineraryExpanded}
                ref={toggleElRef}
                onClick={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4_action: !itineraryExpanded
                            ? "EXPANDED"
                            : "COLLAPSED",
                        ga4_object: "ITINERARY_BUTTON",
                        elementType: "button",
                        clickText: itineraryButtonText,
                    });
                    setItineraryExpanded(!itineraryExpanded);

                    if (!afterFirstClick) {
                        setAfterFirstClick(true);
                    }
                }}
            >
                {itineraryButtonText}
            </Button>
        </section>
    );
};
